import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { PnlChart } from '../components/PnlChart';
import { api } from '../lib/api';
import { costOf, credits, payoutOf, relativeTime } from '../lib/format';

/**
 * Everything the account holder needs to know about their own trading.
 *
 * The ordering is deliberate: money available first, because that is what
 * decides whether they can act; then the record, because that is what decides
 * whether they should.
 *
 * One number is conspicuously absent — a portfolio valuation. Open positions
 * are shown at what they cost, never marked to the last traded price. A market
 * that traded once, thinly, days ago would otherwise show as a profit or a loss
 * that nobody could realise, and the whole point of this page is that its
 * numbers are true.
 */
export function Dashboard() {
  const qc = useQueryClient();
  const summary = useQuery({ queryKey: ['summary'], queryFn: api.summary });
  const performance = useQuery({ queryKey: ['performance'], queryFn: api.performance });
  const positions = useQuery({ queryKey: ['positions'], queryFn: api.positions });
  const orders = useQuery({ queryKey: ['orders'], queryFn: api.orders });

  const cancel = useMutation({
    mutationFn: ({ marketId, orderId }: { marketId: string; orderId: string }) =>
      api.cancelOrder(marketId, orderId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['orders'] });
      void qc.invalidateQueries({ queryKey: ['summary'] });
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });

  const live = (orders.data ?? []).filter((o) => o.state === 'OPEN' || o.state === 'PARTIAL');
  const held = positions.data ?? [];
  const maxPayout = held.reduce((sum, p) => sum + payoutOf(p.qty), 0n);
  const settled = [...(performance.data ?? [])].reverse();

  const pnl = summary.data ? BigInt(summary.data.realisedPnl) : 0n;

  // A brand new account has nothing in any of the five panels below, and five
  // empty boxes is a worse welcome than one sentence telling them what to do.
  // The panels appear as soon as there is anything to put in them.
  const loaded = !summary.isLoading && !positions.isLoading && !orders.isLoading && !performance.isLoading;
  const untouched = loaded && held.length === 0 && live.length === 0 && settled.length === 0;

  return (
    <div className="stack">
      <h1 className="question">Your dashboard</h1>

      {/* ------------------------------------------------ money on hand */}
      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
        <Stat label="Available" value={summary.data ? credits(BigInt(summary.data.cash)) : '—'} />
        <Stat
          label="Held in open orders"
          value={summary.data ? credits(BigInt(summary.data.escrow)) : '—'}
          note={live.length > 0 ? `${live.length} order${live.length === 1 ? '' : 's'}` : undefined}
        />
        <Stat
          label="In open positions"
          value={summary.data ? credits(BigInt(summary.data.openAtCost)) : '—'}
          note="at what you paid"
        />
        <Stat
          label="Positions pay up to"
          value={credits(maxPayout)}
          tone={maxPayout > 0n ? 'good' : undefined}
        />
      </div>

      {untouched && (
        <div className="card">
          <h2>Getting started</h2>
          <p style={{ fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink-2)', margin: '0 0 14px' }}>
            You have 1,000.00 credits and nothing riding on anything yet. Pick a question you have
            a view on, and buy the side you think is right — the price you pay is what the market
            currently thinks the chance is.
          </p>
          <p style={{ fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink-2)', margin: '0 0 16px' }}>
            Every contract pays 100.00 if you are right and nothing if you are wrong. Your record
            and your profit appear on this page once the first market you traded resolves.
          </p>
          <Link to="/" className="submit" data-side="YES" style={{ display: 'inline-block', textDecoration: 'none' }}>
            Browse markets
          </Link>
        </div>
      )}

      {/* ------------------------------------------------ the record */}
      <div className="card" hidden={untouched}>
        <h2>Realised profit</h2>
        {summary.isLoading ? (
          <div className="empty-state">Loading…</div>
        ) : (
          <>
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 14,
                flexWrap: 'wrap',
                marginBottom: 16,
              }}
            >
              {/* The sign is always written out, so direction never depends on
                  colour — see the note in PnlChart about the validator. */}
              <b
                style={{
                  fontFamily: 'var(--mono)',
                  fontSize: 30,
                  letterSpacing: '-0.02em',
                  color: pnl > 0n ? 'var(--good)' : pnl < 0n ? 'var(--critical)' : 'var(--ink)',
                }}
              >
                {pnl > 0n ? '+' : pnl < 0n ? '−' : ''}
                {credits(pnl < 0n ? -pnl : pnl)}
              </b>
              <span style={{ color: 'var(--muted)', fontSize: 13.5 }}>
                across {summary.data?.marketsSettled ?? 0} settled market
                {summary.data?.marketsSettled === 1 ? '' : 's'}
                {summary.data?.winRate !== null && summary.data?.winRate !== undefined && (
                  <>
                    {' · '}
                    {summary.data.wins}–{summary.data.losses} ({summary.data.winRate}% right)
                  </>
                )}
              </span>
            </div>
            <PnlChart points={performance.data ?? []} />
          </>
        )}
      </div>

      {/* ------------------------------------------------ open positions */}
      <div className="card" hidden={untouched}>
        <h2>Positions</h2>
        {held.length === 0 ? (
          <div className="empty-state">
            No positions yet. <Link to="/" style={{ color: 'var(--yes)' }}>Find a market</Link>.
          </div>
        ) : (
          <table className="data">
            <thead>
              <tr><th>Market</th><th>Side</th><th>Contracts</th><th>Pays if right</th><th>Status</th></tr>
            </thead>
            <tbody>
              {held.map((p) => (
                <tr key={`${p.market_id}-${p.side}`}>
                  <td><Link to={`/markets/${p.market_id}`}>{p.question}</Link></td>
                  <td className={p.side === 'YES' ? 'yes' : 'no'}>{p.side}</td>
                  <td className="num">{p.qty.toLocaleString()}</td>
                  <td className="num">{credits(payoutOf(p.qty))}</td>
                  <td className="muted">{p.status === 'OPEN' ? 'Trading' : p.status.toLowerCase()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ------------------------------------------------ open orders */}
      <div className="card" hidden={untouched}>
        <h2>Open orders</h2>
        {live.length === 0 ? (
          <div className="empty-state">No open orders.</div>
        ) : (
          <table className="data">
            <thead>
              <tr><th>Market</th><th>Side</th><th>Price</th><th>Filled</th><th>Held</th><th /></tr>
            </thead>
            <tbody>
              {live.map((o) => (
                <tr key={o.id}>
                  <td><Link to={`/markets/${o.market_id}`}>View market</Link></td>
                  <td className={o.side === 'YES' ? 'yes' : 'no'}>{o.side}</td>
                  <td className="num">{o.price}%</td>
                  <td className="num">{o.filled_qty} / {o.qty}</td>
                  <td className="num">{credits(costOf(o.price, o.qty - o.filled_qty))}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      disabled={cancel.isPending}
                      onClick={() => cancel.mutate({ marketId: o.market_id, orderId: o.id })}
                    >
                      Cancel
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ------------------------------------------------ track record */}
      <div className="card" hidden={untouched}>
        <h2>Settled markets</h2>
        {settled.length === 0 ? (
          <div className="empty-state">
            Nothing has resolved yet. Markets you traded appear here when they settle.
          </div>
        ) : (
          <table className="data">
            <thead>
              <tr><th>Market</th><th>Resolved</th><th>Settled</th><th>Result</th></tr>
            </thead>
            <tbody>
              {settled.map((p) => {
                const value = BigInt(p.pnl);
                return (
                  <tr key={p.marketId}>
                    <td><Link to={`/markets/${p.marketId}`}>{p.question}</Link></td>
                    <td className={p.outcome === 'YES' ? 'yes' : p.outcome === 'NO' ? 'no' : 'muted'}>
                      {p.outcome ?? 'VOID'}
                    </td>
                    <td className="muted">{p.settledAt ? relativeTime(p.settledAt) : '—'}</td>
                    <td
                      className="num"
                      style={{ color: value > 0n ? 'var(--good)' : value < 0n ? 'var(--critical)' : undefined }}
                    >
                      {value > 0n ? '+' : value < 0n ? '−' : ''}
                      {credits(value < 0n ? -value : value)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  note,
  tone,
}: {
  label: string;
  value: string;
  note?: string;
  tone?: 'good';
}) {
  return (
    <div className="card">
      <h2>{label}</h2>
      <div className="prob">
        <b style={{ color: tone === 'good' ? 'var(--good)' : 'var(--ink)' }}>{value}</b>
      </div>
      {note && <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>{note}</div>}
    </div>
  );
}
